import { apiClient } from '@/utils/api-client';
import type {
  DueReport,
  FundReport,
  FundReportCreate,
  FundReportListItem,
  ReportDefinition,
  ReportField,
  ReportTemplate,
  ReportTemplateCreate,
  ReportTemplateListItem,
  ReportTemplateSave,
  ReportNarrative,
} from '@/types/fundReport';

const base = (fundId: string) => `/funds/${fundId}/reports`;

const triggerDownload = (blob: Blob, filename: string): void => {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
};

export const fundReportService = {
  async getReportDefinitions(fundId: string): Promise<ReportDefinition[]> {
    return apiClient.get<ReportDefinition[]>(`${base(fundId)}/definitions`);
  },

  async getReports(fundId: string): Promise<FundReportListItem[]> {
    return apiClient.get<FundReportListItem[]>(base(fundId));
  },

  async getDueReports(fundId: string): Promise<DueReport[]> {
    return apiClient.get<DueReport[]>(`${base(fundId)}/due`);
  },

  async getReport(fundId: string, reportId: string): Promise<FundReport> {
    return apiClient.get<FundReport>(`${base(fundId)}/${reportId}`);
  },

  async generateReport(
    fundId: string,
    data: FundReportCreate
  ): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(base(fundId), data);
  },

  async saveNarrative(
    fundId: string,
    reportId: string,
    narrative: ReportNarrative
  ): Promise<{ id: string }> {
    return apiClient.put<{ id: string }>(
      `${base(fundId)}/${reportId}/narrative`,
      narrative
    );
  },

  async approveReport(
    fundId: string,
    reportId: string
  ): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(
      `${base(fundId)}/${reportId}/approve`
    );
  },

  async publishReport(
    fundId: string,
    reportId: string
  ): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(
      `${base(fundId)}/${reportId}/publish`
    );
  },

  /** Every template this fund may use, with the active one marked. */
  async getReportTemplates(fundId: string): Promise<ReportTemplateListItem[]> {
    return apiClient.get<ReportTemplateListItem[]>(`${base(fundId)}/templates`);
  },

  async getReportTemplate(
    fundId: string,
    templateId: string
  ): Promise<ReportTemplate> {
    return apiClient.get<ReportTemplate>(
      `${base(fundId)}/templates/${templateId}`
    );
  },

  async createReportTemplate(
    fundId: string,
    data: ReportTemplateCreate
  ): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(`${base(fundId)}/templates`, data);
  },

  async updateReportTemplate(
    fundId: string,
    templateId: string,
    data: ReportTemplateSave
  ): Promise<{ id: string }> {
    return apiClient.put<{ id: string }>(
      `${base(fundId)}/templates/${templateId}`,
      data
    );
  },

  /** Make this the template the report type prints for this fund. */
  async putTemplateInUse(
    fundId: string,
    templateId: string
  ): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(
      `${base(fundId)}/templates/${templateId}/use`
    );
  },

  async deleteReportTemplate(
    fundId: string,
    templateId: string
  ): Promise<void> {
    await apiClient.delete(`${base(fundId)}/templates/${templateId}`);
  },

  async getReportFields(
    fundId: string,
    definitionId: string
  ): Promise<ReportField[]> {
    return apiClient.get<ReportField[]>(
      `${base(fundId)}/definitions/${definitionId}/fields`
    );
  },

  downloadTemplateHtml(template: ReportTemplate, filename: string): void {
    triggerDownload(
      new Blob([template.html_content], { type: 'text/html' }),
      filename
    );
  },

  /** The report as it stands now, for review. Nothing is stored. */
  async downloadReportPdf(
    fundId: string,
    reportId: string,
    filename: string
  ): Promise<void> {
    const blob = await apiClient.getBlob(`${base(fundId)}/${reportId}/pdf`);
    triggerDownload(blob, filename);
  },
};
